import cs from "classnames";
import React from "react";
import {
	defineMessages,
	FormattedMessage,
	MessageDescriptor,
	useIntl
} from "react-intl";

import { Event, EventQuery, mapInterpretationToString } from "lib/build/events";

import { Card, CardContent } from "src/common/components/Card";
import { GridColumn, GridRow } from "src/common/components/Grid";
import { Tooltip } from "src/common/components/Tooltip";
import { useFormatFsn } from "src/common/hooks/useFormatFsn";
import { useEventsByQueries } from "src/features/events";
import { useOrgModParams } from "src/features/orgmod";
import { PatientLink } from "src/features/patient";
import { sidToForms } from "src/sidToForms";

import "./MeasurementSummary.css";

const messages = defineMessages({
	measuredTitle: {
		id: "measurement_summary.measured_title",
		defaultMessage: "Kitöltött vizsgálatok"
	},
	expiredTitle: {
		id: "measurement_summary.expired_title",
		defaultMessage: "Lejárt vizsgálatok"
	},
	notMeasuredTitle: {
		id: "measurement_summary.not_measured_title",
		defaultMessage: "Hiányzó kötelező vizsgálatok"
	},
	measuredCount: {
		id: "measurement_summary.measured_count",
		defaultMessage: "{count} kitöltött vizsgálat"
	},
	expiredCount: {
		id: "measurement_summary.expired_count",
		defaultMessage: "{count} lejárt vizsgálat"
	},
	notMeasuredCount: {
		id: "measurement_summary.not_measured_count",
		defaultMessage: "{count} hiányzó vizsgálat"
	},
	noMeasurements: {
		id: "measurement_summary.no_measurements",
		defaultMessage: "Nincsenek vizsgálatok"
	}
});

interface MeasurementSummaryProps {
	groupsOrQueries: (EventQueryGroup | EventQuery)[];
}

interface EventQueryGroup {
	label: MessageDescriptor;
	form: string;
	queries: EventQuery[];
}

enum ItemType {
	Measured,
	Expired,
	NotMeasured
}

interface Item {
	type: ItemType;
	form: string;
	label: string;
}

export const MeasurementSummary: React.FunctionComponent<MeasurementSummaryProps> =
	React.memo(({ groupsOrQueries }) => {
		const { formatMessage } = useIntl();

		const items = useItems(groupsOrQueries);

		const measuredItems = React.useMemo(
			() => selectItemsWithType(items, ItemType.Measured),
			[items]
		);
		const expiredItems = React.useMemo(
			() => selectItemsWithType(items, ItemType.Expired),
			[items]
		);
		const notMeasuredItems = React.useMemo(
			() => selectItemsWithType(items, ItemType.NotMeasured),
			[items]
		);

		const measuredCount = measuredItems.length;
		const expiredCount = expiredItems.length;
		const notMeasuredCount = notMeasuredItems.length;
		const totalCount = measuredCount + expiredCount + notMeasuredCount;

		const measuredWidth = (measuredCount / totalCount) * 100;
		const expiredWidth = (expiredCount / totalCount) * 100;
		const notMeasuredWidth = (notMeasuredCount / totalCount) * 100;

		const [isOpen, setOpen] = React.useState(false);

		const onToggle = React.useCallback(() => {
			setOpen(isOpen => !isOpen);
		}, []);

		return (
			<div
				className={cs("measurement-summary", {
					"measurement-summary--open": isOpen
				})}
			>
				<div className="measurement-progress" onClick={onToggle}>
					<Tooltip
						title={formatMessage(messages.notMeasuredCount, {
							count: notMeasuredCount
						})}
						content={
							<div
								className="measurement-progress__col"
								style={{ width: `${notMeasuredWidth}%` }}
							>
								{notMeasuredItems.length}
							</div>
						}
					/>
					<Tooltip
						title={formatMessage(messages.expiredCount, {
							count: expiredCount
						})}
						content={
							<div
								className="measurement-progress__col"
								style={{ width: `${expiredWidth}%` }}
							>
								{expiredItems.length}
							</div>
						}
					/>
					<Tooltip
						title={formatMessage(messages.measuredCount, {
							count: measuredCount
						})}
						content={
							<div
								className="measurement-progress__col"
								style={{ width: `${measuredWidth}%` }}
							>
								{measuredItems.length}
							</div>
						}
					/>
				</div>
				{(notMeasuredCount !== 0 || expiredCount !== 0) && (
					<div className="measurement-list-wrapper">
						<GridRow>
							{notMeasuredCount !== 0 && (
								<GridColumn
									columnSize={expiredCount === 0 ? 12 : 6}
								>
									<Card>
										<CardContent>
											<div
												className={cs(
													"measurement-list",
													{
														"measurement-list--wide":
															expiredCount === 0
													}
												)}
											>
												<div className="measurement-list__title">
													<FormattedMessage
														{...messages.notMeasuredTitle}
													/>
												</div>
												<div className="measurement-list__body">
													{notMeasuredItems.length ===
													0 ? (
														<div className="measurement-list__no-measurements">
															<FormattedMessage
																{...messages.noMeasurements}
															/>
														</div>
													) : (
														notMeasuredItems.map(
															(item, i) => (
																<PatientLink
																	key={i}
																	to={`/form/${item.form}`}
																	className="measurement-list__item"
																>
																	{item.label}
																</PatientLink>
															)
														)
													)}
												</div>
											</div>
										</CardContent>
									</Card>
								</GridColumn>
							)}
							{expiredCount !== 0 && (
								<GridColumn
									columnSize={notMeasuredCount === 0 ? 12 : 6}
								>
									<Card>
										<CardContent>
											<div
												className={cs(
													"measurement-list",
													{
														"measurement-list--wide":
															notMeasuredCount ===
															0
													}
												)}
											>
												<div className="measurement-list__title">
													<FormattedMessage
														{...messages.expiredTitle}
													/>
												</div>
												<div className="measurement-list__body">
													{expiredItems.length ===
													0 ? (
														<div className="measurement-list__no-measurements">
															<FormattedMessage
																{...messages.noMeasurements}
															/>
														</div>
													) : (
														expiredItems.map(
															(item, i) => (
																<PatientLink
																	key={i}
																	to={`/form/${item.form}`}
																	className="measurement-list__item"
																>
																	{item.label}
																</PatientLink>
															)
														)
													)}
												</div>
											</div>
										</CardContent>
									</Card>
								</GridColumn>
							)}
						</GridRow>
					</div>
				)}
			</div>
		);
	});

function useItems(groupsOrQueries: (EventQueryGroup | EventQuery)[]): Item[] {
	const { mod } = useOrgModParams();
	const { formatMessage } = useIntl();
	const formatFsn = useFormatFsn();

	const itemsA = React.useMemo(
		() =>
			groupsOrQueries.flatMap(item => {
				if ("label" in item) {
					const label = formatMessage(item.label);
					return item.queries.map(query => ({
						form: item.form,
						label,
						query
					}));
				} else {
					let sid = "";
					let label = "";
					if (item.interpretation !== undefined) {
						const tag = item.interpretation;
						sid = mapInterpretationToString(tag);
						label = formatFsn(tag);
					} else if (
						item.tags !== undefined &&
						item.tags.length !== 0
					) {
						const tag = item.tags[0];
						sid = mapInterpretationToString(tag);
						label = formatFsn(tag);
					}
					if (sid === "" || label === "") {
						throw new Error();
					}
					const form = sidToForms[mod][sid][0];
					return { form: form.id, label, query: item };
				}
			}),
		[mod, groupsOrQueries, formatMessage, formatFsn]
	);

	const queries = React.useMemo(
		() => itemsA.map(item => item.query),
		[itemsA]
	);
	const events = useEventsByQueries(queries);

	const itemsB = React.useMemo(() => {
		const map: { [type: number]: { [formId: string]: Item } } = {
			[ItemType.NotMeasured]: {},
			[ItemType.Expired]: {},
			[ItemType.Measured]: {}
		};
		for (let i = 0; i < events.length; i++) {
			const event = events[i];
			let type: ItemType;
			if (event === undefined) {
				type = ItemType.NotMeasured;
			} else if (isEventExpired(event)) {
				type = ItemType.Expired;
			} else {
				type = ItemType.Measured;
			}
			const { form, label } = itemsA[i];
			if (!(form in map[type])) {
				map[type][form] = { type, form, label };
			}
		}
		return Object.values(map).flatMap(o =>
			Object.values(o).sort((a, b) => a.label.localeCompare(b.label))
		);
	}, [itemsA, events]);

	return itemsB;
}

function selectItemsWithType(items: Item[], type: ItemType): Item[] {
	return items.filter(item => item.type === type);
}

function isEventExpired(event: Event): boolean {
	return new Date(event.validTo) < new Date();
}
